import routes from '@/routes';
import {
  Button,
  Grid,
  PasswordInput,
  Select,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/components';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import validate from '@/helpers/validations';
import { useGetAllRolesMutation, useGetUserByIdMutation, useUpdateUserMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';

const UserEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, { open, close }] = useDisclosure(false);
  const [Roles, setRoles] = useState([]);
  const [apiGetAllRoles] = useGetAllRolesMutation();
  const [apiGetUserById] = useGetUserByIdMutation();
  const [apiUpdateUser] = useUpdateUserMutation();

  const USER_STATUS = [
    { value: 'active', label: t('pages.useredit.status.active') },
    { value: 'blocked', label: t('pages.useredit.status.blocked') },
  ];

  const form = useForm({
    initialValues: {
      email: '',
      role: '',
      status: '',
    },
    validate: {
      password: (value) => {
        if (value === undefined || value === '') return null;
        return validate(value, {
          password: t('globals.validations.password'),
        });
      },
      confirmPassword: (value, values) => (value !== values.password ? t('globals.validations.confirmPassword') : null),
      role: (value) => validate(value, { required: t('globals.validations.required') }),
      status: (value) => validate(value, { required: t('globals.validations.required') }),
      email: (value) => validate(value, {
        required: t('globals.validations.required'),
        email: t('globals.validations.email'),
      }),
    },
  });

  useEffect(() => {
    const fetchRoles = async () => {
      const response = await getMutationResult({}, apiGetAllRoles);
      const mappedRoles = response.data.data.map((r) => ({
        value: r._id,
        label: r.name,
      }));
      setRoles(mappedRoles);
    };

    const fetchUser = async () => {
      const response = await getMutationResult({ id }, apiGetUserById);
      const user = response.data.data;

      form.setValues({
        email: user.email,
        role: user.role?._id || '',
        status: user.status,
      });
    };
    fetchRoles();
    fetchUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (values) => {
    try {
      open();

      // eslint-disable-next-line no-unused-vars
      const { confirmPassword, password, ...rest } = values;

      const updatedUser = {
        ...rest,
        id,
      };

      if (password && password !== '') {
        updatedUser.password = password;
      }

      await getMutationResult(updatedUser, apiUpdateUser);

      notifications.show({
        title: t('pages.useredit.notification.title'),
        message: t('pages.useredit.notification.message'),
      });

      navigate(routes.settingsUsers.path);
    } catch (err) {
      notifications.show({
        title: t('global.error'),
        message: err.message || 'Error updating user',
        color: 'red',
      });
    } finally {
      close();
    }
  };

  return (
    <Page hideHelp hideNews title={t('pages.useredit.title')}>
      <form>
        <Grid mb='lg'>
          <Grid.Col span={{ base: 6 }}>
            <TextInput
              autoComplete="off"
              label={t('pages.usercreate.form.email')}
              required
              key={form.key('email')}
              {...form.getInputProps('email')}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <Select
              label={t('pages.usercreate.form.role')}
              data={Roles}
              required
              key={form.key('role')}
              {...form.getInputProps('role')}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <PasswordInput
                    autoComplete="off"
                    label={t('pages.usercreate.form.password')}
                    key={form.key('password')}
                    {...form.getInputProps('password')}
                  />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <PasswordInput
                    autoComplete="off"
                    label={t('pages.usercreate.form.confirmPassword')}
                    key={form.key('confirmPassword')}
                    {...form.getInputProps('confirmPassword')}
                  />
          </Grid.Col>

          <Grid.Col span={{ base: 6 }}>
            <Select
              label={t('pages.usercreate.form.status')}
              data={USER_STATUS}
              required
              key={form.key('status')}
              {...form.getInputProps('status')}
            />
          </Grid.Col>
        </Grid>

        <Button
          loading={loading}
          leftSection={<IconCheck />}
          onClick={form.onSubmit(handleSubmit)}
        >
          {t('globals.save')}
        </Button>
      </form>
    </Page>
  );
};

export default UserEdit;
